import * as Immutable from 'immutable';
import * as t from 'io-ts';

import { ioTsRequired } from '../../shared/helpers/io-ts-helpers';
import { Action, ImmutableInterface } from '../../types/';
import { Example } from '../../actions/EditorActions.types';
interface CodeErrorTab {
  end: any;
  line: any;
  start: any;
  term?: string;
  property?: string;
}

export interface EditorAction {
  tabIndex?: number;
  parentTabIndex?: number;
  code?: any;
  example?: Example;
  examples?: Example[];
  name?: string;
  value?: any;
  childActiveIndex?: number;
  indexChild?: number;
  childId?: string;
  data?: any;
  language?: string;
  newName?: string;
  isMulti?: boolean;
  idParent?: any;
  idChilds: any[];
  editor: any;
  user: any;
  updateAll?: boolean;
  codeError?: CodeErrorTab;
  settings?: any;
  codeType?: string;
  source?: number;
  destination?: number;
  currentActiveTab?: number;
}

export interface BaseEditorStore {
  editor: EditorStoreElement;
}
export type EditorStore = ImmutableInterface<BaseEditorStore>;

interface BaseEditorTab {
  tabName: string;
  language?: string;
  code?: string;
  combo?: string;
  selection?: string;
  superAlpha?: boolean;
  childTabs?: Immutable.List<EditorTab>;
  settings: SettingOptions;
  activeChildTab?: number;
  childTabCount: number;
  id: string;
  visualization: boolean;
  codeError: null | CodeErrorTab;
  learnPathname: string;
  tabNumber: string;
}
export type EditorTab = ImmutableInterface<BaseEditorTab>;

interface BaseSettingOptions {
  region?: string;
  universe?: string;
  delay?: string;
  decay?: string;
  neutralization?: string;
  truncation?: string;
}
export type SettingOptions = ImmutableInterface<BaseSettingOptions>;

interface BaseEditorStoreElement {
  tabs: Immutable.List<EditorTab>;
  activeTab?: number;
  tabCount?: number;
  multiTabCount?: number;
  superTabCount?: number;
  exampleArrayIds?: Immutable.List<string>;
  invalidIds?: any;
  userId?: string;
  isMonacoInitialized: boolean;
  options: any;
  fetchingSettings: boolean;
  loadedOptions: boolean;
  codeEditorWidth: number;
  isChangingTabPosition: boolean;
  dataExplorerPathname: string;
  tutorialPathname: string;
  learnPathname: string;
  examples?: Immutable.List<ImmutableInterface<Example>>;
  isSimulated: boolean;
}
export type EditorStoreElement = ImmutableInterface<BaseEditorStoreElement>;

export type EditorActionType = Action<EditorAction>;

export const SimulateMetadataChoices = t.interface({
  label: t.string,
  value: t.union([t.string, t.number]),
});

const region = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
  choices: t.interface({
    instrumentType: t.dictionary(t.string, t.array(SimulateMetadataChoices)),
  }),
});

const instrumentType = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
  choices: t.array(SimulateMetadataChoices),
});

const regionChoices = t.interface({
  region: t.dictionary(t.string, t.array(SimulateMetadataChoices)),
});

const universe = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
  choices: t.interface({
    instrumentType: t.dictionary(t.string, regionChoices),
  }),
});

const decay = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
  minValue: t.number,
});

const neutralization = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
  choices: t.interface({
    instrumentType: t.dictionary(t.string, regionChoices),
  }),
});

const truncation = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
});

const language = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
  choices: t.array(SimulateMetadataChoices),
});

const visualization = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
});

const delay = t.interface({
  type: t.string,
  required: t.boolean,
  readOnly: t.boolean,
  choices: t.interface({
    instrumentType: t.dictionary(t.string, regionChoices),
  }),
});

export const SimulationsValidation = t.interface({
  actions: t.interface({
    POST: t.interface({
      settings: t.interface({
        children: t.intersection([
          t.interface({
            region,
            universe,
            delay,
            decay,
            neutralization,
            truncation,
            language,
            visualization,
            instrumentType,
          }),
          t.partial({
            lookback: t.interface({
              type: t.string,
              required: t.boolean,
              readOnly: t.boolean,
              label: t.string,
              minValue: t.number,
              maxValue: t.number,
            }),
          }),
        ]),
      }),
    }),
  }),
});

const Settings = t.interface({
  region: ioTsRequired,
  universe: ioTsRequired,
  delay: t.Integer,
  decay: t.Integer,
  neutralization: ioTsRequired,
  truncation: t.number,
  language: ioTsRequired,
  pasteurization: t.union([t.string, t.undefined]),
  unitHandling: t.union([t.string, t.undefined]),
  nanHandling: t.union([t.string, t.undefined]),
  // As saving settings will default empty fields to null
  // For settings controlled by permissions, please make sure that you allow **null** for these settings
  componentActivation: t.union([t.string, t.undefined, t.null]),
  testPeriod: t.union([t.string, t.undefined, t.null]),
  maxTrade: t.union([t.string, t.undefined, t.null]),
  maxPosition: t.union([t.string, t.undefined, t.null]),
  lookback: t.union([t.number, t.undefined, t.null]),
});

export const SimulationsPostValidation = t.interface({
  type: ioTsRequired,
  settings: Settings,
  code: t.union([ioTsRequired, t.undefined]),
});
